import {
  Users,
  MessageSquare,
  ArrowRight,
  Plus,
  Trash2,
  Play,
  Settings,
  Network,
  Clock,
  CheckCircle,
  AlertTriangle,
  Info
} from 'lucide-react'
import React, { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { Agent } from '@/stores/agentStore'

interface AgentCollaborationDialogProps {
  isOpen: boolean
  onClose: () => void
  agentId: string
  agentName: string
  projectId: string
}

interface CollaborationTeam {
  id: string
  name: string
  description: string
  members: AgentTeamMember[]
  workflow: WorkflowStep[]
  status: 'active' | 'paused' | 'completed' | 'error'
  createdAt: string
  lastRun?: string
}

interface AgentTeamMember {
  agentId: string
  agentName: string
  role: string
  permissions: string[]
  status: 'active' | 'inactive' | 'busy'
}

interface WorkflowStep {
  id: string
  order: number
  agentId: string
  agentName: string
  action: string
  inputs: string[]
  outputs: string[]
  dependencies: string[]
  conditions?: string
  timeout?: number
  retries?: number
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped'
}

interface AgentMessage {
  id: string
  fromAgentId: string
  fromAgentName: string
  toAgentId: string
  toAgentName: string
  message: string
  data?: any
  timestamp: string
  type: 'info' | 'request' | 'response' | 'error'
  read: boolean
}

const AgentCollaborationDialog: React.FC<AgentCollaborationDialogProps> = ({
  isOpen,
  onClose,
  agentId,
  agentName,
  projectId
}) => {
  const [activeTab, setActiveTab] = useState('teams')
  const [teams, setTeams] = useState<CollaborationTeam[]>([])
  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(false)

  // Team creation state
  const [showCreateTeam, setShowCreateTeam] = useState(false)
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
    members: [] as AgentTeamMember[]
  })

  // Message state
  const [showSendMessage, setShowSendMessage] = useState(false)
  const [newMessage, setNewMessage] = useState({
    toAgentId: '',
    message: '',    type: 'info' as 'info' | 'request' | 'response' | 'error'
  })

  // Workflow builder state - TODO: Implement workflow builder functionality
  // const [selectedTeam, setSelectedTeam] = useState<CollaborationTeam | null>(null)
  // const [showWorkflowBuilder, setShowWorkflowBuilder] = useState(false)

  // Fetch collaboration data
  const fetchCollaborationData = async () => {
    if (!agentId || !projectId) return
    
    setLoading(true)
    try {
      // Fetch teams
      const teamsResponse = await fetch(`/api/projects/${projectId}/agents/${agentId}/teams`)
      if (teamsResponse.ok) {
        const teamsData = await teamsResponse.json()
        setTeams(teamsData)
      }

      // Fetch messages
      const messagesResponse = await fetch(`/api/projects/${projectId}/agents/${agentId}/messages`)
      if (messagesResponse.ok) {
        const messagesData = await messagesResponse.json()
        setMessages(messagesData)
      }

      // Fetch available agents
      const agentsResponse = await fetch(`/api/projects/${projectId}/agents`)
      if (agentsResponse.ok) {
        const agentsData = await agentsResponse.json()
        setAvailableAgents(agentsData.filter((agent: Agent) => agent.id !== agentId))
      }
    } catch (error) {
      console.error('Failed to fetch collaboration data:', error)
      
      // Mock data for development
      setTeams([
        {
          id: '1',
          name: 'Content Creation Team',
          description: 'Collaborative content creation and review workflow',
          members: [
            {
              agentId: agentId,
              agentName: agentName,
              role: 'Content Creator',
              permissions: ['create', 'edit'],
              status: 'active'
            },
            {
              agentId: 'agent-2',
              agentName: 'Review Agent',
              role: 'Content Reviewer',
              permissions: ['review', 'approve'],
              status: 'active'
            }
          ],
          workflow: [
            {
              id: 'step-1',
              order: 1,
              agentId: agentId,
              agentName: agentName,
              action: 'Create Initial Content',
              inputs: ['prompt', 'guidelines'],
              outputs: ['draft_content'],
              dependencies: [],
              status: 'completed'
            },
            {
              id: 'step-2',
              order: 2,
              agentId: 'agent-2',
              agentName: 'Review Agent',
              action: 'Review Content',
              inputs: ['draft_content'],
              outputs: ['reviewed_content', 'feedback'],
              dependencies: ['step-1'],
              status: 'running'
            }
          ],
          status: 'active',
          createdAt: new Date().toISOString(),
          lastRun: new Date().toISOString()
        }
      ])

      setMessages([
        {
          id: '1',
          fromAgentId: 'agent-2',
          fromAgentName: 'Review Agent',
          toAgentId: agentId,
          toAgentName: agentName,
          message: 'Content draft looks good! Minor suggestions for improvement.',
          timestamp: new Date().toISOString(),
          type: 'info',
          read: false
        }
      ])

      setAvailableAgents([
        {
          id: 'agent-2',
          name: 'Review Agent',
          type: 'reviewer',
          enabled: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          projectId: projectId,
          triggerEvents: [],
          observationList: []
        }
      ])
    } finally {
      setLoading(false)
    }
  }

  // Create new team
  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      toast.error('Team name is required')
      return
    }

    try {
      const response = await fetch(`/api/projects/${projectId}/agents/${agentId}/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newTeam.name,
          description: newTeam.description,
          members: [
            {
              agentId: agentId,
              agentName: agentName,
              role: 'Team Leader',
              permissions: ['create', 'edit', 'manage'],
              status: 'active'
            },
            ...newTeam.members
          ]
        })
      })

      if (response.ok) {
        toast.success('Team created successfully!')
        setShowCreateTeam(false)
        setNewTeam({ name: '', description: '', members: [] })
        fetchCollaborationData()
      } else {
        throw new Error('Failed to create team')
      }
    } catch (error) {
      console.error('Failed to create team:', error)
      toast.error('Failed to create team. Please try again.')
    }
  }

  // Send message to agent
  const handleSendMessage = async () => {
    if (!newMessage.toAgentId || !newMessage.message.trim()) {
      toast.error('Please select recipient and enter message')
      return
    }

    try {
      const response = await fetch(`/api/projects/${projectId}/agents/${agentId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          toAgentId: newMessage.toAgentId,
          message: newMessage.message,
          type: newMessage.type
        })
      })

      if (response.ok) {
        toast.success('Message sent successfully!')
        setShowSendMessage(false)
        setNewMessage({ toAgentId: '', message: '', type: 'info' })
        fetchCollaborationData()
      } else {
        throw new Error('Failed to send message')
      }
    } catch (error) {
      console.error('Failed to send message:', error)
      toast.error('Failed to send message. Please try again.')
    }
  }

  // Add agent to team
  const handleAddAgentToTeam = (agentToAdd: Agent) => {
    setNewTeam(prev => ({
      ...prev,
      members: [
        ...prev.members,
        {
          agentId: agentToAdd.id,
          agentName: agentToAdd.name,
          role: 'Team Member',
          permissions: ['execute'],
          status: 'active'
        }
      ]
    }))
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'completed':
        return 'bg-green-500'
      case 'running':
        return 'bg-blue-500'
      case 'paused':
        return 'bg-yellow-500'
      case 'error':
      case 'failed':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />
      case 'running':
        return <Play className="w-4 h-4" />
      case 'error':
      case 'failed':
        return <AlertTriangle className="w-4 h-4" />
      default:
        return <Clock className="w-4 h-4" />
    }
  }

  // Load data when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchCollaborationData()
    }
  }, [isOpen, agentId, projectId])

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Agent Collaboration - {agentName}</span>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="teams">Teams & Workflows</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="network">Network View</TabsTrigger>
          </TabsList>

          {/* Teams & Workflows Tab */}
          <TabsContent value="teams" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Collaboration Teams</h3>
              <Button onClick={() => setShowCreateTeam(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Create Team
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {teams.map((team) => (
                    <Card key={team.id} className="hover:shadow-md transition-shadow">
                      <CardHeader hasCardAction={false} className="">
                        <CardTitle className="flex items-center justify-between">
                          <span className="flex items-center space-x-2">
                            <Users className="w-5 h-5" />
                            <span>{team.name}</span>
                          </span>
                          <div className="flex items-center space-x-2">
                            <Badge className={`${getStatusColor(team.status)} text-white`}>
                              {team.status}
                            </Badge>                            <Button size="sm" variant="outline" onClick={() => {
                              // TODO: Implement workflow builder
                              console.log('Workflow builder for team:', team.id)
                            }}>
                              <Settings className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-3">{team.description}</p>
                        
                        <div className="space-y-3">
                          <div>
                            <h4 className="font-medium mb-2">Team Members ({team.members.length})</h4>
                            <div className="flex flex-wrap gap-2">
                              {team.members.map((member, index) => (
                                <Badge key={index} variant="outline" className="flex items-center space-x-1">
                                  <div className={`w-2 h-2 rounded-full ${getStatusColor(member.status)}`} />
                                  <span>{member.agentName}</span>
                                  <span className="text-xs">({member.role})</span>
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <div>
                            <h4 className="font-medium mb-2">Workflow Progress ({team.workflow.length} steps)</h4>
                            <div className="space-y-1">
                              {team.workflow.slice(0, 3).map((step) => (
                                <div key={step.id} className="flex items-center space-x-2 text-sm">
                                  {getStatusIcon(step.status)}
                                  <span>{step.order}. {step.action}</span>
                                  <span className="text-gray-500">({step.agentName})</span>
                                </div>
                              ))}
                              {team.workflow.length > 3 && (
                                <div className="text-xs text-gray-500">
                                  +{team.workflow.length - 3} more steps...
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Agent Messages</h3>
              <Button onClick={() => setShowSendMessage(true)}>
                <MessageSquare className="w-4 h-4 mr-1" />
                Send Message
              </Button>
            </div>

            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {messages.map((message) => (
                  <Card key={message.id} className={`${!message.read ? 'border-blue-200 bg-blue-50' : ''}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <Badge variant={message.type === 'error' ? 'destructive' : 'outline'}>
                              {message.type}
                            </Badge>
                            <span className="font-medium">{message.fromAgentName}</span>
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">{message.toAgentName}</span>
                          </div>
                          <p className="text-sm mb-2">{message.message}</p>
                          {message.data && (
                            <div className="bg-gray-100 p-2 rounded text-xs">
                              <strong>Data:</strong> {JSON.stringify(message.data, null, 2)}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(message.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Network View Tab */}
          <TabsContent value="network" className="space-y-4">
            <div className="flex items-center space-x-2 mb-4">
              <Network className="w-5 h-5" />
              <h3 className="text-lg font-semibold">Agent Network</h3>
            </div>
            
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Network visualization showing agent connections and data flow will be implemented here.
                This will display a graph view of how agents communicate and collaborate.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>

        {/* Create Team Dialog */}
        {showCreateTeam && (
          <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam} modal>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Team</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Team name"
                  value={newTeam.name}
                  onChange={(e) => setNewTeam(prev => ({ ...prev, name: e.target.value }))}
                />
                <Textarea
                  placeholder="Team description"
                  value={newTeam.description}
                  onChange={(e) => setNewTeam(prev => ({ ...prev, description: e.target.value }))}
                />
                
                <div>
                  <h4 className="font-medium mb-2">Add Team Members</h4>
                  <div className="space-y-2">
                    {availableAgents.map((agent) => (
                      <div key={agent.id} className="flex items-center justify-between p-2 border rounded">
                        <span>{agent.name}</span>
                        <Button size="sm" onClick={() => handleAddAgentToTeam(agent)}>
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {newTeam.members.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Selected Members</h4>
                    <div className="space-y-1">
                      {newTeam.members.map((member, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <span>{member.agentName} - {member.role}</span>
                          <Button size="sm" variant="outline" onClick={() => {
                            setNewTeam(prev => ({
                              ...prev,
                              members: prev.members.filter((_, i) => i !== index)
                            }))
                          }}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex space-x-2 justify-end">
                  <Button variant="outline" onClick={() => setShowCreateTeam(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateTeam}>
                    Create Team
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Send Message Dialog */}
        {showSendMessage && (
          <Dialog open={showSendMessage} onOpenChange={setShowSendMessage} modal>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Send Message</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <Select value={newMessage.toAgentId} onValueChange={(value) => 
                  setNewMessage(prev => ({ ...prev, toAgentId: value }))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select recipient agent" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAgents.map((agent) => (
                      <SelectItem key={agent.id} value={agent.id}>
                        {agent.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={newMessage.type} onValueChange={(value: 'info' | 'request' | 'response' | 'error') => 
                  setNewMessage(prev => ({ ...prev, type: value }))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Message type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Information</SelectItem>
                    <SelectItem value="request">Request</SelectItem>
                    <SelectItem value="response">Response</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                  </SelectContent>
                </Select>

                <Textarea
                  placeholder="Enter your message..."
                  value={newMessage.message}
                  onChange={(e) => setNewMessage(prev => ({ ...prev, message: e.target.value }))}
                  rows={4}
                />

                <div className="flex space-x-2 justify-end">
                  <Button variant="outline" onClick={() => setShowSendMessage(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSendMessage}>
                    Send Message
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AgentCollaborationDialog
